// The exam report over time (namat#112): every completed exam's indicators,
// one series per indicator from the oldest exam to the newest, with the change
// since the previous exam and whether it moved toward the good level. Values
// and levels come from readIndicators only; unmeasured values (stored as 0)
// are never plotted. Pure and identical in web and mobile
// (src/lib/assessment/examTrends.ts); change both.

import {
  ageInMonths,
  INDICATORS,
  readIndicators,
  type ExamValues,
  type IndicatorReading,
  type PersonInput,
} from './namatAssessment';
import { distanceToGood, indicatorBands, type IndicatorBands } from './indicatorBands';

export type ReportExam = ExamValues & { id?: string; date: Date; status?: string };

export interface TrendPoint {
  date: Date;
  examId?: string;
  /** One value per line (blood pressure: systolic, diastolic); null when that part was not measured. */
  values: (number | null)[];
  reading: IndicatorReading;
}

export type ChangeDirection = 'better' | 'worse' | 'neutral';

export type ChangeMarker =
  | { kind: 'first' }
  | { kind: 'same' }
  /** deltas: latest minus previous per line, to one decimal; null when a side is missing. */
  | { kind: 'changed'; deltas: (number | null)[]; direction: ChangeDirection };

export interface IndicatorSeries {
  key: IndicatorReading['key'];
  unit: string;
  /** Lines in the chart: 2 for blood pressure, otherwise 1. */
  lines: number;
  /** Oldest first; only exams where this indicator was measured. */
  points: TrendPoint[];
  latest: IndicatorReading;
  change: ChangeMarker;
  /** Level bands at the latest exam's age, per line; null when the indicator has none. */
  bands: IndicatorBands | null;
}

export interface ExamReport {
  /** Completed exams, oldest first. */
  exams: { id?: string; date: Date }[];
  series: IndicatorSeries[];
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** A reading's value as numbers: "120/80" → [120, 80]; a missing part ("-") → null. */
export function readingValues(value: number | string): (number | null)[] {
  if (typeof value === 'number') return [Number.isFinite(value) && value > 0 ? value : null];
  return value.split('/').map((part) => {
    const n = parseFloat(part);
    return Number.isFinite(n) && n > 0 ? n : null;
  });
}

const ageAt = (person: PersonInput, date: Date) => (person.birthDate ? ageInMonths(person.birthDate, date) : null);

/** Sum over lines of the distance to the good band; null when nothing can be compared. */
function distance(bands: IndicatorBands | null, values: (number | null)[], other: (number | null)[]): number | null {
  if (!bands) return null;
  let total = 0;
  let any = false;
  values.forEach((v, i) => {
    if (v === null || other[i] == null || !bands[i]) return;
    const d = distanceToGood(bands[i], v);
    if (d === null) return;
    total += d;
    any = true;
  });
  return any ? total : null;
}

function changeOf(key: string, points: TrendPoint[], person: PersonInput): ChangeMarker {
  if (points.length < 2) return { kind: 'first' };
  const prev = points[points.length - 2];
  const last = points[points.length - 1];
  const deltas = last.values.map((v, i) => {
    const p = prev.values[i];
    return v === null || p == null ? null : round1(v - p);
  });
  const compared = deltas.filter((d): d is number => d !== null);
  if (compared.length === 0) return { kind: 'first' };
  if (compared.every((d) => d === 0)) return { kind: 'same' };

  const before = distance(indicatorBands(key, { ageMonths: ageAt(person, prev.date), sex: person.sex }), prev.values, last.values);
  const after = distance(indicatorBands(key, { ageMonths: ageAt(person, last.date), sex: person.sex }), last.values, prev.values);
  let direction: ChangeDirection = 'neutral';
  if (before !== null && after !== null) {
    if (after < before - 1e-9) direction = 'better';
    else if (after > before + 1e-9) direction = 'worse';
  }
  return { kind: 'changed', deltas, direction };
}

const order = (key: string) => {
  const i = (INDICATORS as string[]).indexOf(key);
  return i === -1 ? INDICATORS.length : i;
};

/**
 * The report of one beneficiary: completed exams oldest → newest and, for every
 * indicator readIndicators gives in any of them, its series. Indicators keep
 * the INDICATORS order; ones it does not list follow in the order first seen.
 */
export function buildExamReport(exams: ReportExam[], person: PersonInput = {}): ExamReport {
  const done = exams
    .filter((e) => e.status === 'completed' && e.date instanceof Date && Number.isFinite(e.date.getTime()))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const byKey = new Map<string, TrendPoint[]>();
  for (const exam of done) {
    const readings = readIndicators(exam, { ageMonths: ageAt(person, exam.date), sex: person.sex });
    for (const reading of readings) {
      const values = readingValues(reading.value);
      if (values.every((v) => v === null)) continue;
      const list = byKey.get(reading.key) ?? [];
      list.push({ date: exam.date, examId: exam.id, values, reading });
      byKey.set(reading.key, list);
    }
  }

  const seen = [...byKey.keys()];
  const keys = seen.sort((a, b) => order(a) - order(b) || seen.indexOf(a) - seen.indexOf(b));
  const latestDate = done.length ? done[done.length - 1].date : null;
  const series: IndicatorSeries[] = keys.map((key) => {
    const points = byKey.get(key)!;
    const latest = points[points.length - 1];
    return {
      key: latest.reading.key,
      unit: latest.reading.unit,
      lines: Math.max(...points.map((p) => p.values.length)),
      points,
      latest: latest.reading,
      change: changeOf(key, points, person),
      bands: indicatorBands(key, { ageMonths: latestDate ? ageAt(person, latestDate) : null, sex: person.sex }),
    };
  });

  return { exams: done.map((e) => ({ id: e.id, date: e.date })), series };
}

/** A number as the health cards show it: whole numbers bare, otherwise one decimal. */
export function formatMeasure(n: number): string {
  return n.toFixed(n % 1 === 0 ? 0 : 1);
}

/**
 * The change as text without words: "↓ 0.8", "↑ 3", blood pressure "↓ 5/2"
 * (both down) or "↓ 5/↑ 2". Empty for a first or unchanged reading, which the
 * screens label themselves («أول قياس», «ثابت»).
 */
export function formatChange(marker: ChangeMarker, fmt: (n: number) => string = formatMeasure): string {
  if (marker.kind !== 'changed') return '';
  const arrow = (d: number) => (d < 0 ? '↓' : '↑');
  const moved = marker.deltas.filter((d): d is number => d !== null && d !== 0);
  const oneWay = moved.every((d) => Math.sign(d) === Math.sign(moved[0]));
  if (oneWay) {
    return `${arrow(moved[0])} ${marker.deltas.map((d) => (d === null ? '–' : fmt(Math.abs(d)))).join('/')}`;
  }
  return marker.deltas.map((d) => (d === null ? '–' : d === 0 ? '0' : `${arrow(d)} ${fmt(Math.abs(d))}`)).join('/');
}

/**
 * The y range of a chart: every plotted value plus the finite band edges, so
 * the good band shows next to the readings, padded by a tenth; never below 0.
 */
export function chartDomain(series: Pick<IndicatorSeries, 'points' | 'bands'>): { min: number; max: number } {
  const values = series.points.flatMap((p) => p.values).filter((v): v is number => v !== null);
  const edges = (series.bands ?? []).flat().flatMap((b) => [b.from, b.to]).filter((v) => v > 0 && Number.isFinite(v));
  const all = [...values, ...edges];
  if (all.length === 0) return { min: 0, max: 1 };
  let min = Math.min(...all);
  let max = Math.max(...all);
  const pad = Math.max((max - min) * 0.1, max === min ? Math.max(Math.abs(max) * 0.1, 1) : 0);
  min = Math.max(0, min - pad);
  max = max + pad;
  return { min: round1(min), max: round1(max) };
}
